import React, { useState } from "react";
import { StatCard } from "../StateCard";
import {
  ArrowUpDown,
  BarChart3,
  Calendar,
  CheckCircle,
  Send,
  Users,
  X,
  XCircle,
  Zap,
} from "lucide-react";
import { RootDispatch, RootState } from "../../../store";
import { useDispatch, useSelector } from "react-redux";
import { ListData } from "./ListData";
import { setSelectDateForPerformance } from "../../../store/slices/roundRobinSlice";
import ReloadButton from "../../AssetsComponent/ReloadButton";

interface WhatsAppAutoMessageSenderDashboardProps {
  handleRefresh: () => void;
  isRefreshing: boolean;
}

export const WhatsAppAutoMessageSenderDashboard: React.FC<
  WhatsAppAutoMessageSenderDashboardProps
> = ({ handleRefresh, isRefreshing }) => {
  const [showDatePicker, setShowDatePicker] = useState(false);

  const formatDisplayDate = (dateString: string) => {
    const date = new Date(dateString);
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    if (dateString === today.toISOString().split("T")[0]) {
      return "Today";
    } else if (dateString === yesterday.toISOString().split("T")[0]) {
      return "Yesterday";
    } else {
      return date.toLocaleDateString("en-US", {
        weekday: "short",
        year: "numeric",
        month: "short",
        day: "numeric",
      });
    }
  };

  const report = useSelector((state: RootState) => state.roundRobin.report);
  const reportForToday = useSelector(
    (state: RootState) => state.roundRobin.reportForToday,
  );
  const dispatch: RootDispatch = useDispatch();
  const calculateSuccessRate = (success: number, total: number) => {
    if (!total) return 0;
    return Math.round((success / total) * 100);
  };
  const selectDateForPerformance = useSelector(
    (state: RootState) => state.roundRobin.selectDateForPerformance,
  );
  const getTodayDate = () => {
    return new Date().toISOString().split("T")[0];
  };

  return (
    <div className="min-h-screen  text-slate-800 px-6 md:px-10">
      <div className="max-w-7xl mx-auto space-y-12">
        {/* Header: All-Time Stats */}
        <section className="space-y-6">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 bg-blue-600 text-white rounded-lg flex items-center justify-center shadow-sm">
                <BarChart3 className="w-5 h-5" />
              </div>
              <div>
                <h2 className="text-xl font-bold">All-Time Performance</h2>
                <p className="text-sm text-slate-500">Cumulative metrics</p>
              </div>
            </div>
            <ReloadButton onReload={handleRefresh} />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <StatCard
              icon={Send}
              title="Total Messages Sent"
              value={report?.totalSent!}
              subtitle="All time"
              color="bg-blue-500"
              theme="blue"
            />
            <StatCard
              icon={CheckCircle}
              title="Successful Deliveries"
              value={report?.totalSuccess!}
              subtitle={`${calculateSuccessRate(
                report?.totalSuccess!,
                report?.totalSent!,
              )}% success rate`}
              color="bg-green-500"
              theme="blue"
            />
            <StatCard
              icon={XCircle}
              title="Failed Messages"
              value={report?.totalFailed!}
              subtitle="Error tracking"
              color="bg-red-500"
              theme="blue"
            />
            <StatCard
              icon={Users}
              title="Total Read"
              value={report?.totalRead!}
              subtitle="Currently engaged"
              color="bg-purple-500"
              theme="blue"
            />
          </div>
        </section>

        {/* <div className="py-10"></div> */}

        <section className="space-y-6 ">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 bg-emerald-600 text-white rounded-lg flex items-center justify-center shadow-sm">
                <Zap className="w-5 h-5" />
              </div>
              <div>
                <h2 className="text-xl font-bold">
                  Daily Report – {formatDisplayDate(selectDateForPerformance)}
                </h2>
                <p className="text-sm text-slate-500">Live daily stats</p>
              </div>
            </div>

            {/* Date Picker Button */}
            <div className="relative">
              <button
                onClick={() => setShowDatePicker(!showDatePicker)}
                className="inline-flex items-center gap-2 px-4 py-2 bg-white border border-slate-300 rounded-lg shadow-sm hover:bg-slate-50 text-sm font-medium transition"
              >
                <Calendar className="w-4 h-4 text-slate-600" />
                {formatDisplayDate(selectDateForPerformance)}
                <ArrowUpDown className="w-3 h-3 text-slate-400" />
              </button>

              {/* Floating Date Picker */}
              {showDatePicker && (
                <div className="absolute z-50 right-0 mt-2 w-[280px] bg-white border border-slate-200 rounded-xl shadow-lg p-4">
                  <div className="flex justify-between items-center mb-3">
                    <h4 className="text-sm font-medium text-slate-700">
                      Select a Date
                    </h4>
                    <button
                      onClick={() => setShowDatePicker(false)}
                      className="hover:bg-slate-100 p-1 rounded-md"
                    >
                      <X className="w-4 h-4 text-slate-500" />
                    </button>
                  </div>
                  <input
                    type="date"
                    value={selectDateForPerformance}
                    onChange={(e) => {
                      dispatch(setSelectDateForPerformance(e.target.value));
                      setShowDatePicker(false);
                    }}
                    max={getTodayDate()}
                    className="w-full border border-slate-200 px-3 py-2 rounded-md text-sm focus:outline-none focus:ring focus:ring-blue-500"
                  />
                  <button
                    onClick={() => {
                      dispatch(setSelectDateForPerformance(getTodayDate()));
                      setShowDatePicker(false);
                    }}
                    className="w-full mt-3 text-sm text-center bg-slate-100 hover:bg-slate-200 px-3 py-2 rounded-md"
                  >
                    Today
                  </button>
                </div>
              )}
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-6">
            <StatCard
              icon={Send}
              title="Messages Sent"
              value={reportForToday?.totalSent!}
              subtitle="Since midnight"
              color="bg-blue-500"
              theme="green"
            />
            <StatCard
              icon={CheckCircle}
              title="Successful"
              value={reportForToday?.totalSuccess!}
              subtitle={`${calculateSuccessRate(
                reportForToday?.totalSuccess!,
                reportForToday?.totalSent!,
              )}% delivery rate`}
              color="bg-green-500"
              theme="green"
            />
            <StatCard
              icon={XCircle}
              title="Failed"
              value={reportForToday?.totalFailed!}
              subtitle="Error rate"
              color="bg-red-500"
              theme="green"
            />
            <StatCard
              icon={Users}
              title="Read Messages"
              value={reportForToday?.totalRead!}
              subtitle="Read rate"
              color="bg-purple-500"
              theme="green"
            />
          </div>
        </section>

        {/* Section: Detailed Table */}
      </div>
      <section className="mt-24">
        <ListData />
      </section>
    </div>
  );
};
